import { Button, zonedInstant } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { errorOf } from "../../api";
import { categoriesQuery, useProductItems } from "../../catalog";
import { useCompanyFormat } from "../../company";
import { placesQuery } from "../../customers";
import { fleetQueries } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { uploadContract } from "../../files";
import { BASIS, type Basis, chargeTypesQuery, pricingActions, zonesQuery } from "../../pricing";
import { type Payer, PayerPicker } from "../invoices/payer";
import styles from "./pricing.module.css";

/** An amount the API takes: up to 2 places; a percent is 0–100. */
export const amountOk = (amount: string, basis: Basis) =>
  /^\d{1,12}(\.\d{1,2})?$/.test(amount.trim()) &&
  (basis !== "percent" || /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/.test(amount.trim()));

/** When something takes effect: now, or the start of a day in the company's zone. */
export function MomentField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Date | null;
  onChange: (day: Date | null) => void;
}) {
  return (
    <div className={styles.form}>
      <SegmentedControl
        label={label}
        value={value ? "day" : "now"}
        onValueChange={(v) => onChange(v === "now" ? null : new Date(new Date().setHours(0, 0, 0, 0) + 86_400_000))}
        options={[
          { value: "now", label: "Now" },
          { value: "day", label: "From a day" },
        ]}
      />
      {value ? (
        <DatePicker label="Day" value={value} onChange={(d) => onChange(d ?? null)} minDate={new Date()} />
      ) : null}
    </div>
  );
}

/** When a rule stops: never, or the start of a day (it applies until then). */
export function EndField({ value, onChange }: { value: Date | null; onChange: (day: Date | null) => void }) {
  return (
    <div className={styles.form}>
      <SegmentedControl
        label="Ends"
        value={value ? "day" : "never"}
        onValueChange={(v) =>
          onChange(v === "never" ? null : new Date(new Date().setHours(0, 0, 0, 0) + 30 * 86_400_000))
        }
        options={[
          { value: "never", label: "No end" },
          { value: "day", label: "On a day" },
        ]}
      />
      {value ? (
        <DatePicker
          label="Ends at the start of"
          value={value}
          onChange={(d) => onChange(d ?? null)}
          minDate={new Date()}
        />
      ) : null}
    </div>
  );
}

/** The instant for a MomentField's value: undefined is now (the API's default). */
export const momentOf = (day: Date | null, timezone: string) =>
  day ? zonedInstant(day, "00:00", timezone) : undefined;

/**
 * A new rule: a charge type applied to a scope, from now or a day. Leave a scope empty for
 * everyone; the most specific rule wins when several match.
 */
export function RuleDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const format = useCompanyFormat();
  const types = useQuery(chargeTypesQuery);
  const zones = useQuery(zonesQuery);
  const items = useProductItems();
  const [typeId, setTypeId] = useState("");
  const [basis, setBasis] = useState<Basis>("per_unit");
  const [amount, setAmount] = useState("");
  const [itemId, setItemId] = useState("any");
  const [zoneId, setZoneId] = useState("any");
  const [fillsiteId, setFillsiteId] = useState("any");
  const fillsites = useQuery(fleetQueries.fillsites);
  const [payer, setPayer] = useState<Payer | null>(null);
  const [placeId, setPlaceId] = useState("any");
  const [categoryId, setCategoryId] = useState("any");
  const [from, setFrom] = useState<Date | null>(null);
  const [until, setUntil] = useState<Date | null>(null);
  const [priority, setPriority] = useState("");
  const [contract, setContract] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const categories = useQuery(categoriesQuery);
  const places = useQuery({ ...placesQuery(payer?.billee ?? { customer_id: "" }), enabled: Boolean(payer) });
  const create = useMutation({
    mutationFn: async () => {
      // the signed contract goes up first; the rule points at it
      const file = contract ? await uploadContract(contract) : null;
      return pricingActions.create({
        charge_type_id: typeId,
        basis,
        amount: amount.trim(),
        ...(itemId !== "any" ? { product_item_id: itemId } : {}),
        ...(categoryId !== "any" ? { category_id: categoryId } : {}),
        ...(zoneId !== "any" ? { geofence_id: zoneId } : {}),
        ...(fillsiteId !== "any" ? { fillsite_id: fillsiteId } : {}),
        ...(payer ? payer.billee : {}),
        ...(placeId !== "any" ? { location_id: placeId } : {}),
        starts_at: momentOf(from, format.timezone),
        ends_at: momentOf(until, format.timezone),
        // number-ok: a rule's priority, a small whole number, not money
        ...(priority.trim() ? { priority: Number.parseInt(priority.trim(), 10) } : {}),
        ...(file ? { contract_file_id: file.id } : {}),
        notes: notes.trim() || undefined,
      });
    },
    onSuccess: async (rule) => {
      await queryClient.invalidateQueries({ queryKey: ["pricing"] });
      onClose();
      void navigate({ to: "/pricing/$ruleId", params: { ruleId: rule.id } });
    },
  });
  const active = (types.data ?? []).filter((t) => t.is_active);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="New pricing rule"
        description="Leave a scope as Any to apply it to everyone. The most specific rule wins: place, customer or business, fill site, zone, item."
      >
        <div className={styles.form}>
          <div className={styles.pair}>
            <Select
              label="Charge"
              placeholder="Choose a charge type"
              value={typeId}
              onValueChange={setTypeId}
              options={active.map((t) => ({ value: t.id, label: t.name }))}
            />
            <Select
              label="Basis"
              value={basis}
              onValueChange={(v) => setBasis(v as Basis)}
              options={(Object.keys(BASIS) as Basis[]).map((b) => ({ value: b, label: BASIS[b] }))}
            />
          </div>
          <Input
            label={basis === "percent" ? "Percent" : "Amount"}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            description={basis === "percent" ? "0 to 100" : `In ${format.currency}. 0 makes an exemption.`}
          />
          <div className={styles.pair}>
            <Select
              label="Item"
              value={itemId}
              disabled={categoryId !== "any"}
              onValueChange={setItemId}
              options={[
                { value: "any", label: "Any item" },
                ...items.items.map((i) => ({ value: i.id, label: i.label })),
              ]}
            />
            <Select
              label="Category"
              description="Every item of the category's products"
              value={categoryId}
              disabled={itemId !== "any"}
              onValueChange={setCategoryId}
              options={[
                { value: "any", label: "Any category" },
                ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            <Select
              label="Zone"
              value={zoneId}
              onValueChange={setZoneId}
              options={[
                { value: "any", label: "Any zone" },
                ...(zones.data ?? []).map((z) => ({ value: z.id, label: z.name })),
              ]}
            />
            <Select
              label="Fill site"
              description="Its own rate, as water. It applies once a delivery has this fill site."
              value={fillsiteId}
              onValueChange={setFillsiteId}
              options={[
                { value: "any", label: "Any fill site" },
                ...(fillsites.data ?? []).map((f) => ({ value: f.id, label: f.name })),
              ]}
            />
          </div>
          <details className={styles.details}>
            <summary>For one customer or business (a contract price)</summary>
            <div className={styles.form}>
              <PayerPicker
                value={payer}
                onChange={(p) => {
                  setPayer(p);
                  setPlaceId("any");
                }}
              />
              {payer ? (
                <Select
                  label="Place"
                  value={placeId}
                  onValueChange={setPlaceId}
                  options={[
                    { value: "any", label: "All their places" },
                    ...(places.data ?? []).map((l) => ({ value: l.id, label: l.label })),
                  ]}
                />
              ) : null}
            </div>
          </details>
          <MomentField label="Starts" value={from} onChange={setFrom} />
          <EndField value={until} onChange={setUntil} />
          <details className={styles.details}>
            <summary>Priority and contract (optional)</summary>
            <div className={styles.form}>
              <Input
                label="Priority"
                inputMode="numeric"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                description="When two rules are equally specific, the higher priority wins. -1000 to 1000; default 0."
                error={priority.trim() && !/^-?\d{1,4}$/.test(priority.trim()) ? "A whole number." : undefined}
              />
              <label className={styles.fileField}>
                <span>Signed contract (PDF, up to 20 MB)</span>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setContract(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          </details>
          <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          {create.error ? (
            <Alert tone="danger" title="Not added">
              {messageFor(errorOf(create.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={
                !typeId || !amountOk(amount, basis) || Boolean(priority.trim() && !/^-?\d{1,4}$/.test(priority.trim()))
              }
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Add rule
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
